import React, { useState, useEffect } from 'react';
import { Table, Button, Card, Form, Row, Col, Badge, Modal } from 'react-bootstrap';
import { PlusIcon, EditIcon, TrashIcon, WarningIcon } from '../common/Icons';
import { productService } from '../../services/api';
import { Product, PaginatedResponse } from '../../types';
import { toast } from 'react-toastify';
import ProductForm from './ProductForm';

const ProductList: React.FC = () => {
  const [products, setProducts] = useState<PaginatedResponse<Product> | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showLowStock, setShowLowStock] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState<Product | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (search) params.search = search;
      if (showLowStock) params.low_stock = true;

      const response = await productService.getAll(params);
      setProducts(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des produits');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [search, showLowStock]);

  const handleDelete = async () => {
    if (!productToDelete) return;

    try {
      await productService.delete(productToDelete.id);
      toast.success('Produit supprimé avec succès');
      fetchProducts();
      setShowDeleteModal(false);
      setProductToDelete(null);
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const getStockBadge = (product: Product) => {
    if (product.current_stock <= 0) {
      return <Badge bg="danger">Rupture</Badge>;
    } else if (product.current_stock <= product.minimum_stock) {
      return <Badge bg="warning">Stock faible</Badge>;
    } else {
      return <Badge bg="success">En stock</Badge>;
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Gestion des produits</h1>
        <Button variant="primary" onClick={() => setShowAddModal(true)}>
          <PlusIcon className="me-2" />
          Nouveau produit
        </Button>
      </div>

      <Card>
        <Card.Header>
          <Row>
            <Col md={6}>
              <Form.Control
                type="text"
                placeholder="Rechercher un produit..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Col>
            <Col md={6}>
              <Form.Check
                type="checkbox"
                label="Afficher uniquement les produits en stock faible"
                checked={showLowStock}
                onChange={(e) => setShowLowStock(e.target.checked)}
              />
            </Col>
          </Row>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="text-center">Chargement...</div>
          ) : (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Référence</th>
                  <th>Nom</th>
                  <th>Catégorie</th>
                  <th>Prix de vente HT</th>
                  <th>Stock actuel</th>
                  <th>Stock minimum</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {products?.data.map((product) => (
                  <tr key={product.id}>
                    <td>{product.reference}</td>
                    <td>
                      {product.name}
                      {product.current_stock <= product.minimum_stock && (
                        <WarningIcon className="text-warning ms-2" />
                      )}
                    </td>
                    <td>{product.category?.name || '-'}</td>
                    <td>
                      {new Intl.NumberFormat('fr-FR', {
                        style: 'currency',
                        currency: 'MAD'
                      }).format(product.selling_price_ht)}
                    </td>
                    <td>{product.current_stock}</td>
                    <td>{product.minimum_stock}</td>
                    <td>{getStockBadge(product)}</td>
                    <td>
                      <Button variant="outline-primary" size="sm" className="me-2">
                        <EditIcon />
                      </Button>
                      <Button 
                        variant="outline-danger" 
                        size="sm"
                        onClick={() => {
                          setProductToDelete(product);
                          setShowDeleteModal(true);
                        }}
                      >
                        <TrashIcon />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Modal de confirmation de suppression */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirmer la suppression</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Êtes-vous sûr de vouloir supprimer le produit "{productToDelete?.name}" ?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Annuler
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Supprimer
          </Button>
        </Modal.Footer>
      </Modal>

      <ProductForm
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        onSuccess={fetchProducts}
      />
    </div>
  );
};

export default ProductList;